import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import { Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { HorizontalLocalMenu } from '@praktio/common-ui';

import Report from '../../common/Report/Report';
import PageHeader from '../../common/PageHeader/PageHeader';
import ReportSettings from '../../common/ReportSettings/ReportSettings';
import OverlayProgressBar from '../../common/OverlayProgressBar/OverlayProgressBar';
import ReportSettingsPane from '../../admin/GeneralReport/ReportSettingsPane/ReportSettingsPane';
import TableSummaryReport from '../../admin/GeneralReport/TableSummaryReport/TableSummaryReport';
import ReportNotLoaded from '../../common/ReportNotLoaded/ReportNotLoaded';
import ReportAccessError from '../../common/ReportAccessError/ReportAccessError';

import { RootState } from '../../../ducks/store';
import { actions as teamActions } from '../../../ducks/teams';
import { useGetTeamsSlice } from '../../../hooks/useGetTeamsSlice';
import {
  clearReportData,
  updateReportSettings,
  fetchTeamManagerReportBatch,
  selectCanRegenerateReport,
} from '../../../ducks/report';
import { useFetchCoursesOnLoad } from '../../../hooks/useFetchCoursesOnLoad';
import { useBatchedReport, BatchFetchFn } from '../../../hooks/useBatchedReport';
import { useOverlayTransition } from '../../../hooks/useOverlayTransition';

import 'react-datepicker/dist/react-datepicker.css';
import './TeamManagerReport.scss';

const TeamManagerReport = () => {
  // ===========================================================================
  // State
  // ===========================================================================

  const [notificationsSaving, setNotificationsSaving] = useState(false);

  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const {
    activeTeams,
    activeDeselectedCourses,
    reportSettingsPaneOpen,
    teams,
    user,
    batchIsLoading,
    batchProgress,
    batchError,
    batchCanRetry,
    runRequestId,
    data,
  } = useSelector((state: RootState) => ({
    activeTeams: state.teamsSlice.activeTeams,
    activeDeselectedCourses: state.teamsSlice.activeDeselectedCourses,
    reportSettingsPaneOpen: state.report.reportSettingsPaneOpen,
    teams: state.teams.list,
    user: state.auth.authenticatedUser,
    batchIsLoading: state.report.batchIsLoading,
    batchProgress: state.report.batchProgress,
    batchError: state.report.batchError,
    batchCanRetry: state.report.batchCanRetry,
    runRequestId: state.report.runRequestId,
    data: state.report.data,
  }));

  const canRegenerate = useSelector(selectCanRegenerateReport);

  // ===========================================================================
  // Hooks
  // ===========================================================================

  useFetchCoursesOnLoad();

  const { noCoursesSelected } = useGetTeamsSlice();

  // ===========================================================================
  // Batched Report
  // ===========================================================================

  const fetchFn: BatchFetchFn = useCallback(
    async (teamId: string, signal: AbortSignal) => {
      return fetchTeamManagerReportBatch(teamId, activeDeselectedCourses, signal);
    },
    [activeDeselectedCourses],
  );

  const { trigger, abort, cancel } = useBatchedReport({
    batchKeys: activeTeams,
    fetchFn,
  });

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleSave = async (notification?: IReportNotification) => {
    setNotificationsSaving(true);

    try {
      await dispatch(
        updateReportSettings({
          user: { ...user, reportNotification: notification },
        }),
      );
    } finally {
      setNotificationsSaving(false);
    }
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    dispatch({ type: teamActions.GET_TEAMS, payload: user?.teams });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (noCoursesSelected) {
      dispatch(clearReportData());
      return;
    }
  }, [noCoursesSelected, dispatch]);

  // Runs the report: on arrival, whenever the committed selection changes, and whenever the user
  // presses Run. runRequestId covers the last of those on its own, so pressing Run with an
  // unchanged selection still loads the report.
  useEffect(() => {
    if (noCoursesSelected) return;

    abort();
    trigger();
  }, [noCoursesSelected, activeTeams, activeDeselectedCourses, runRequestId, trigger, abort]);

  const {
    showOverlay,
    isComplete,
    handleComplete: handleOverlayComplete,
  } = useOverlayTransition(batchIsLoading, canRegenerate);

  // Absent, not falsy: a failure carrying an empty message is still a failure, and treating it as
  // one keeps an errored run from being announced as politely as a cancellation the user chose.
  const hasError = batchError != null;

  // A run that ended without data. Which of the two messages the canvas shows turns on batchCanRetry:
  // a cancel and an ordinary transport failure get the not-loaded canvas, while a lapsed session,
  // lost access, and anything the classifier could not place get told to sign in rather than sent to
  // a Run button that will fail the same way. See canRetryAfter for what actually lands where.
  const showNotLoaded = !showOverlay && canRegenerate && batchCanRetry;
  const showAccessError = !showOverlay && canRegenerate && !batchCanRetry;
  const showReportData = !showOverlay && !canRegenerate;

  return (
    <div className="TeamManagerReport GeneralReport d-flex flex-column">
      <Helmet>
        <title>Reports - Praktio</title>
      </Helmet>

      <PageHeader pageName="Reports" className="mb-0">
        <ReportSettings
          notification={user?.reportNotification}
          isSaving={notificationsSaving}
          onSaveClick={handleSave}
        />
      </PageHeader>

      <div className="GeneralReport_MainContainer">
        <ReportSettingsPane teams={teams} />

        <HorizontalLocalMenu
          classes={classNames({
            'HorizontalLocalMenu--open': reportSettingsPaneOpen,
          })}
          defaultActiveKey="summaryView"
        >
          <Tab eventKey="summaryView" title="Summary View">
            {showOverlay ? (
              <OverlayProgressBar
                progress={batchProgress}
                isComplete={isComplete}
                onCancel={cancel}
                onComplete={handleOverlayComplete}
              />
            ) : null}

            {showNotLoaded ? <ReportNotLoaded hasError={hasError} /> : null}

            {showAccessError ? <ReportAccessError /> : null}

            {showReportData ? <TableSummaryReport reportData={data} /> : null}
          </Tab>

          <Tab eventKey="detailView" title="Detail View">
            {showOverlay ? (
              <OverlayProgressBar
                progress={batchProgress}
                isComplete={isComplete}
                onCancel={cancel}
                onComplete={handleOverlayComplete}
              />
            ) : null}

            {showNotLoaded ? <ReportNotLoaded hasError={hasError} /> : null}

            {showAccessError ? <ReportAccessError /> : null}

            {showReportData ? <Report user={user} reportData={data} teams={teams} /> : null}
          </Tab>
        </HorizontalLocalMenu>
      </div>
    </div>
  );
};

export default TeamManagerReport;
